import { useEffect, useState } from 'react';
import { cn } from '@/client/lib/utils';
import { Button } from '@/client/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/client/components/ui/dialog';
import { Input } from '@/client/components/ui/input';
import { Label } from '@/client/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/client/components/ui/select';
import type { RouterOutputs } from '@/client/utils/trpc';

type Employee = RouterOutputs['employees']['list'][number];

export type PersonFormState = {
  email: string;
  full_name: string;
  job_title: string;
  rate: string;
  rate_type: '' | 'hourly' | 'monthly';
};

const emptyForm: PersonFormState = {
  email: '',
  full_name: '',
  job_title: '',
  rate: '',
  rate_type: '',
};

function Field({
  label,
  id,
  className,
  children,
}: {
  label: string;
  id: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('space-y-1.5', className)}>
      <Label htmlFor={id}>{label}</Label>
      {children}
    </div>
  );
}

interface PersonDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  person: Employee | null;
  onSubmit: (form: PersonFormState) => void;
  isPending: boolean;
  error: string | null;
}

export function PersonDialog({
  open,
  onOpenChange,
  person,
  onSubmit,
  isPending,
  error,
}: PersonDialogProps) {
  const [form, setForm] = useState<PersonFormState>(emptyForm);

  useEffect(() => {
    if (open) {
      setForm(
        person
          ? {
              email: person.email,
              full_name: person.full_name ?? '',
              job_title: person.job_title ?? '',
              rate: person.rate != null ? String(person.rate) : '',
              rate_type: person.rate_type ?? '',
            }
          : emptyForm,
      );
    }
  }, [open, person]);

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    onSubmit(form);
  }

  const isAdd = person === null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isAdd ? 'Invite Person' : 'Edit Person'}</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          {isAdd && (
            <Field label="Email" id="person-email">
              <Input
                id="person-email"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              />
            </Field>
          )}
          <Field label="Full Name" id="person-name">
            <Input
              id="person-name"
              required
              value={form.full_name}
              onChange={(e) => setForm((f) => ({ ...f, full_name: e.target.value }))}
            />
          </Field>
          <Field label="Job Title" id="person-title">
            <Input
              id="person-title"
              value={form.job_title}
              onChange={(e) => setForm((f) => ({ ...f, job_title: e.target.value }))}
            />
          </Field>
          <div className="flex gap-3">
            <Field label="Rate" id="person-rate" className="flex-1">
              <Input
                id="person-rate"
                type="number"
                min="0"
                step="0.01"
                value={form.rate}
                onChange={(e) => setForm((f) => ({ ...f, rate: e.target.value }))}
              />
            </Field>
            <Field label="Type" id="person-rate-type" className="flex-1">
              <Select
                value={form.rate_type}
                onValueChange={(v) =>
                  setForm((f) => ({ ...f, rate_type: v as 'hourly' | 'monthly' }))
                }
              >
                <SelectTrigger id="person-rate-type">
                  <SelectValue placeholder="Select…" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="hourly">Hourly</SelectItem>
                  <SelectItem value="monthly">Monthly</SelectItem>
                </SelectContent>
              </Select>
            </Field>
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Saving…' : isAdd ? 'Invite' : 'Save'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
